import { forwardRef, useMemo } from 'react';
import { SuggestionList } from '../components/SuggestionList';
import { SuggestionPopover } from '../components/SuggestionPopover';
import {
  type MergeField,
  type CategorizedMergeField,
  filterMergeField,
  useMergeFields,
  isCategorizedMergeFieldList,
} from '../use-merge-fields';
import { insertMergeField } from '../utils/commands';

import { ReactRenderer } from '@tiptap/react';
import type { SuggestionListHandle } from '../components/SuggestionList';
import type { SuggestionOptions, SuggestionProps } from '@tiptap/suggestion';

interface MergeFieldListProps extends SuggestionProps {
  mergeFields?: MergeField[] | CategorizedMergeField[];
  listHeaders?: boolean;
  portalTarget: Element;
  zIndex?: number;
}

export const MergeFieldList = forwardRef<
  SuggestionListHandle,
  MergeFieldListProps
>(
  (
    {
      editor,
      range,
      query,
      mergeFields,
      listHeaders,
      decorationNode,
      portalTarget,
      zIndex,
    },
    ref
  ) => {
    const { data, isLoading } = useMergeFields({
      mergeFields,
      listHeaders,
    });

    const items = useMemo(() => {
      if (!isCategorizedMergeFieldList(data)) {
        return data.filter(filterMergeField(query.trim()));
      }

      return data
        .map(({ label, items, key }) => {
          return {
            label,
            key,
            items: items?.length
              ? items.filter(filterMergeField(query.trim()))
              : [],
          };
        })
        .filter(({ items }) => items.length);
    }, [data, query]);

    return (
      <SuggestionPopover
        target={decorationNode}
        portalTarget={portalTarget}
        width="200px"
        zIndex={zIndex}
      >
        <SuggestionList
          ref={ref}
          onSelect={(mf: MergeField) => {
            editor
              .chain()
              .focus()
              .setTextSelection(range)
              .command(insertMergeField(mf))
              .run();
          }}
          items={items}
          isLoading={isLoading}
        />
      </SuggestionPopover>
    );
  }
);

export const getMergeFieldSuggestionOptions = (
  opts: {
    appendToSelector?: Element | string;
    props?: Record<string, any>;
    zIndex?: number;
  } = {}
): Omit<SuggestionOptions, 'editor'> => {
  return {
    render() {
      let reactRenderer: ReactRenderer<SuggestionListHandle, any>;

      return {
        onStart: (props) => {
          const appendToNode =
            typeof opts.appendToSelector === 'string'
              ? props.decorationNode?.closest(opts.appendToSelector) ??
                document.querySelector(opts.appendToSelector)
              : opts.appendToSelector;
          const portalTarget = appendToNode || document.body;

          reactRenderer = new ReactRenderer(MergeFieldList, {
            props: {
              ...props,
              ...opts.props,
              portalTarget,
              zIndex: opts?.zIndex,
            },
            editor: props.editor,
          });
        },
        onUpdate(props) {
          reactRenderer?.updateProps(props);
        },
        onKeyDown(props) {
          const { event } = props;

          if (!reactRenderer) {
            return false;
          }

          if (event.key === 'Escape') {
            reactRenderer.destroy();

            return true;
          }

          return reactRenderer.ref?.onKeyDown(props) ?? false;
        },
        onExit() {
          reactRenderer?.destroy();
        },
      };
    },
  };
};
